import {
    computeRcdFloatingPosition,
    getRcdVisualViewport,
    parseRcdCssLength
} from "../floating";

const setupMarker = Symbol.for("rcd.popover.setup");

const resolveTrigger = (target) => (
    target instanceof Element
        ? target.closest("[data-rcd-popover-trigger]")
        : null
);

const resolveSurface = (target) => {
    if (target instanceof HTMLElement && target.matches("[data-rcd-popover]")) {
        return target;
    }

    const trigger = resolveTrigger(target);
    const id = trigger?.getAttribute("popovertarget")
        || trigger?.getAttribute("aria-controls")
        || trigger?.dataset.rcdPopoverTarget;
    return id ? document.getElementById(id) : null;
};

export const setupRcdPopovers = () => {
    if (window[setupMarker]) {
        return window.rcdPopover;
    }

    window[setupMarker] = true;
    let active = null;
    let positionFrame = 0;

    const setExpanded = (trigger, expanded) => {
        if (trigger) {
            trigger.setAttribute("aria-expanded", String(expanded));
        }
    };

    const findTrigger = (surface) => (
        document.querySelector(`[data-rcd-popover-trigger][popovertarget="${CSS.escape(surface.id)}"]`)
        || document.querySelector(`[data-rcd-popover-trigger][aria-controls="${CSS.escape(surface.id)}"]`)
        || document.querySelector(`[data-rcd-popover-trigger][data-rcd-popover-target="${CSS.escape(surface.id)}"]`)
    );

    const positionActive = () => {
        positionFrame = 0;
        if (!active?.surface.isConnected || !active?.trigger.isConnected) {
            return;
        }

        const rootStyle = getComputedStyle(document.documentElement);
        const gap = parseRcdCssLength(rootStyle.getPropertyValue("--rcd-space-2"), 8);
        const padding = parseRcdCssLength(rootStyle.getPropertyValue("--rcd-space-3"), 12);
        const direction = getComputedStyle(active.trigger).direction === "rtl" ? "rtl" : "ltr";
        const result = computeRcdFloatingPosition({
            anchorRect: active.trigger.getBoundingClientRect(),
            surfaceRect: active.surface.getBoundingClientRect(),
            placement: active.surface.dataset.rcdPopoverPlacement || "block-end",
            direction,
            viewport: getRcdVisualViewport(),
            padding,
            gap
        });

        active.surface.style.left = `${result.left}px`;
        active.surface.style.top = `${result.top}px`;
        active.surface.dataset.rcdPopoverSide = result.side;
        active.surface.dataset.rcdPopoverResolvedPlacement = result.placement;
        active.surface.style.setProperty("--rcd-popover-arrow-left", `${result.arrowLeft}px`);
        active.surface.style.setProperty("--rcd-popover-arrow-top", `${result.arrowTop}px`);
    };

    const refresh = () => {
        if (!active || positionFrame) {
            return;
        }

        positionFrame = window.requestAnimationFrame(positionActive);
    };

    const close = (target = active?.surface) => {
        const surface = resolveSurface(target);
        if (!(surface instanceof HTMLElement)) {
            return false;
        }

        if (typeof surface.hidePopover === "function" && surface.matches(":popover-open")) {
            surface.hidePopover();
        } else {
            surface.hidden = true;
            surface.dataset.state = "closed";
            setExpanded(findTrigger(surface), false);
        }
        return true;
    };

    const open = (target) => {
        const surface = resolveSurface(target);
        const trigger = resolveTrigger(target) || (surface ? findTrigger(surface) : null);
        if (!(surface instanceof HTMLElement) || !(trigger instanceof HTMLElement)) {
            return false;
        }

        if (typeof surface.showPopover === "function") {
            if (!surface.matches(":popover-open")) {
                surface.showPopover();
            }
        } else {
            surface.hidden = false;
            surface.dataset.state = "opening";
            active = { surface, trigger };
            positionActive();
            window.requestAnimationFrame(() => {
                surface.dataset.state = "open";
            });
        }
        setExpanded(trigger, true);
        return true;
    };

    document.addEventListener("beforetoggle", (event) => {
        const surface = event.target;
        if (!(surface instanceof HTMLElement) || !surface.matches("[data-rcd-popover]")) {
            return;
        }

        const trigger = findTrigger(surface);
        if (event.newState === "open" && trigger instanceof HTMLElement) {
            active = { surface, trigger };
            surface.dataset.state = "opening";
            setExpanded(trigger, true);
            window.requestAnimationFrame(() => {
                if (active?.surface === surface) {
                    positionActive();
                    surface.dataset.state = "open";
                }
            });
            return;
        }

        setExpanded(trigger, false);
        surface.dataset.state = "closed";
        if (active?.surface === surface) {
            active = null;
        }
    }, true);

    document.addEventListener("click", (event) => {
        const trigger = resolveTrigger(event.target);
        if (!trigger || typeof HTMLElement.prototype.showPopover === "function") {
            return;
        }

        event.preventDefault();
        const surface = resolveSurface(trigger);
        if (surface?.hidden === false) {
            close(surface);
        } else {
            open(trigger);
        }
    });

    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && active && typeof active.surface.hidePopover !== "function") {
            close(active.surface);
            active.trigger.focus({ preventScroll: true });
        }
    });

    const cleanupWithin = (target) => {
        if (!(target instanceof Element) || !active) {
            return;
        }

        if (target === active.surface || target.contains(active.surface) || target.contains(active.trigger)) {
            close(active.surface);
            active = null;
        }
    };

    document.addEventListener("htmx:beforeSwap", (event) => cleanupWithin(event.detail?.target));
    document.addEventListener("htmx:beforeCleanupElement", (event) => cleanupWithin(event.target));
    window.addEventListener("resize", refresh, { passive: true });
    window.addEventListener("scroll", refresh, { passive: true, capture: true });
    window.visualViewport?.addEventListener("resize", refresh, { passive: true });
    window.visualViewport?.addEventListener("scroll", refresh, { passive: true });

    window.rcdPopover = { open, close, refresh };
    return window.rcdPopover;
};
